import React from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { endpoints } from "../../endpoints";
import { ExportableTable } from "./ExportableTable";
import { isSafeExportHref } from "./tableExport";
import type { ExportCell } from "./tableExport";
import type { ResultTablePreview, ResultTableResponse } from "./types";

export function ResultTable({
  table,
  tenantId,
  messageId,
  chatBotName,
}: {
  table: ResultTablePreview;
  tenantId?: string;
  messageId?: string;
  chatBotName?: string;
}) {
  const { t } = useTranslation();
  if (!table.available) {
    return (
      <p role="status">
        {t(
          "This saved table is no longer available with your current access. Run the search again."
        )}
      </p>
    );
  }

  const previewRows = table.preview_rows.slice(0, 25);
  const savedAt = new Date(table.created_at);
  const loadRows = async (): Promise<ExportCell[][]> => {
    if (!tenantId || !messageId) throw new Error("Missing saved message");
    const { data } = await axios.get<ResultTableResponse>(
      endpoints.v2_tenants_id_agenticSearch_message_table(
        tenantId,
        messageId,
        table.id
      )
    );
    // Never silently fall back to the 25 DOM rows after a failed or partial
    // download. Both formats must represent the same complete snapshot.
    if (
      data.id !== table.id ||
      !data.complete ||
      data.total_rows !== table.total_rows ||
      !Array.isArray(data.rows) ||
      data.rows.length !== table.total_rows ||
      JSON.stringify(data.columns) !== JSON.stringify(table.columns) ||
      JSON.stringify(data.rows.slice(0, previewRows.length)) !==
        JSON.stringify(previewRows) ||
      data.rows.some(
        (row) =>
          !Array.isArray(row) ||
          row.length !== table.columns.length ||
          row.some(
            (cell) =>
              !cell ||
              typeof cell.text !== "string" ||
              (cell.href !== undefined && !isSafeExportHref(cell.href))
          )
      )
    ) {
      throw new Error("Incomplete or mismatched saved table");
    }
    return [
      data.columns.map((column) => ({ text: column.label })),
      ...data.rows,
    ];
  };

  return (
    <section aria-label={table.title}>
      <strong>{table.title}</strong>
      <p>
        {t("Showing {{shown}} of {{total}} rows", {
          shown: previewRows.length,
          total: table.total_rows,
        })}
      </p>
      {!Number.isNaN(savedAt.getTime()) && (
        <small>
          <time dateTime={table.created_at}>
            {t("Saved {{date}}", { date: savedAt.toLocaleString() })}
          </time>
        </small>
      )}
      <ExportableTable
        loadRows={loadRows}
        totalRows={table.total_rows}
        chatBotName={chatBotName}
      >
        <thead>
          <tr>
            {table.columns.map((column) => (
              <th key={column.key} scope="col">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {previewRows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, column) => (
                <td key={table.columns[column].key}>
                  {isSafeExportHref(cell.href) ? (
                    <a href={cell.href}>{cell.text}</a>
                  ) : (
                    cell.text
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </ExportableTable>
      {table.note && (
        <p>
          <small>{table.note}</small>
        </p>
      )}
    </section>
  );
}
